/**
 * CREDIT NOTES and SALES RETURNS — post-sale adjustment of a valid sale.
 *
 *   Quotation → Sales Order → Delivery Note → Sales Invoice → Receipt
 *                                        ↓
 *                              Credit Note / Sales Return
 *
 * THE TWO TYPES ARE ONE SERVICE ON PURPOSE
 * ---------------------------------------
 * `CREDIT_NOTE` and `SALES_RETURN` already exist as distinct document types with
 * distinct `DocTypeSpec`s, and the business distinction is real and must survive:
 *
 *   CREDIT NOTE  — a FINANCIAL reduction against a valid sale. Nothing physical
 *                  happens. `movesStock: false`, so issuing it posts no movement.
 *   SALES RETURN — goods physically come back. `movesStock: true,
 *                  stockDirection: 'IN'`, so issuing it posts an IN movement that
 *                  the return document OWNS.
 *
 * Every other part of the mechanics is identical: both correct a source invoice,
 * both snapshot it, both are bounded by what was sold, both are drafted then
 * issued. Two services would duplicate all of that and let the copies drift, so
 * there is one service parameterised by `docType`, and the type alone decides
 * whether stock moves. That is exactly how `docTypes.ts` already models it.
 *
 * THIS IS NOT CANCELLATION
 * ------------------------
 * Cancellation means the original document was WRONG and is being reversed; the
 * existing `cancelIssuedDocument` already owns that. A return means the sale was
 * VALID and the customer has since brought goods back. They are different events
 * with different audit trails, and the app must never reach for one to do the
 * other's job. In particular a return does NOT touch the invoice's own stock
 * movement: that OUT already happened when the sale was made, and the return
 * records its own separate IN.
 */
import type { DNRDatabase } from '@data/db/database';
import type { Document, DocumentLine } from '@data/db/types';
import { docSpec, type DocType } from '@domain/docTypes';
import { asQuantity } from '@core/money';
import { resolvePlaceOfSupply } from '@core/gst/split';
import { getLineAvailability } from '@domain/fulfilment';
import { issueDocument, type IssueLineInput, type IssueParams } from './documentService';
import { issueDraftDocument } from './issueTransitionService';
import { listRecentDocuments, loadDocument, type RecentDocumentsPage } from './documentReader';
import { loadBusinessIdentityWithImages } from './businessReader';
import { resolveActiveBusiness } from './businessSession';
import { currentDeviceId as getDeviceId } from '@data/sync/deviceIdentity';
import { nextVersion, recordBusinessMutations } from '@data/sync/recordBusinessMutation';
import { derivePaymentStatus } from '@domain/lifecycle';
import { outstandingPaise, sumIssuedCredits, sumLiveAllocations } from './paymentService';
import { ulid } from '../../platform/ids';

const USER = { id: 'local', name: 'You' };

/** The two document types this service produces. */
export type ReturnDocType = Extract<DocType, 'CREDIT_NOTE' | 'SALES_RETURN'>;

/**
 * True when issuing this type physically brings goods back into stock.
 *
 * Deliberately a comparison rather than a second copy of the rule: `docTypes.ts`
 * already owns "does this type move stock", and a duplicate of that flag here is
 * a second thing to forget when a document type changes.
 */
export function returnsGoods(docType: ReturnDocType): boolean {
  return docType === 'SALES_RETURN';
}

/** The label a credit note or return is shown as, by type. */
export function returnTypeLabel(docType: ReturnDocType): string {
  return docSpec(docType).label;
}

export type ReturnServiceErrorCode =
  | 'NO_BUSINESS'
  | 'SOURCE_NOT_FOUND'
  | 'SOURCE_NOT_ISSUED'
  | 'WRONG_BUSINESS'
  | 'WRONG_SOURCE_TYPE'
  | 'NOT_FOUND'
  | 'NOT_EDITABLE'
  | 'NO_LINES'
  | 'NO_RETURNABLE_LINES'
  | 'INVALID_LINES'
  | 'INVALID_DATE'
  | 'SOURCE_LINE_INVALID'
  | 'INVALID_PLACE_OF_SUPPLY'
  | 'STOCK_LOCATION_REQUIRED'
  | 'INVALID_DOC_TYPE'
  | 'HAS_CHILDREN';

export class ReturnServiceError extends Error {
  constructor(
    readonly code: ReturnServiceErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'ReturnServiceError';
  }
}

export interface ReturnLineInput {
  readonly sourceLineId: string;
  readonly quantityMilli: number;
  /**
   * Free quantity being returned, for a "buy one get one" sale.
   *
   * Counted toward the returnable total rather than ignored: a free unit was
   * billed at zero, so crediting it back is not a refund, but it IS goods that
   * physically came back and must still be bounded by what was sold.
   */
  readonly freeQuantityMilli?: number;
}

export interface ReturnDocumentInput {
  readonly docType: ReturnDocType;
  readonly invoiceId: string;
  readonly docDate: string;
  readonly lines: readonly ReturnLineInput[];
  readonly reason?: string;
  readonly notes?: string;
  readonly now?: string;
  readonly deviceId?: string;
}

/** An invoice line, seen from the side that wants to return it. */
export interface ReturnableLine extends DocumentLine {
  /** How much of this line may still be returned or credited. */
  readonly returnableQtyMilli: number;
  readonly alreadyReturnedQtyMilli: number;
  readonly isStockTracked: boolean;
}

export interface ReturnSource {
  readonly invoice: Document;
  readonly lines: readonly ReturnableLine[];
}

function assertDate(value: string): void {
  const parsed = new Date(`${value}T00:00:00Z`);
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(value) ||
    Number.isNaN(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== value
  ) {
    throw new ReturnServiceError('INVALID_DATE', 'Enter a valid return date.');
  }
}

/**
 * The place-of-supply check, re-used from the invoice's own frozen answer.
 *
 * A credit note or return is a TAX document (`taxDocument: true`), so issuing one
 * creates a GST liability and must not proceed on an unresolvable state. The check
 * runs at CREATE rather than at issue for the same reason the invoice service
 * does it there: an invalid state is REPORTED to the user, never silently
 * replaced with a different one.
 */
function checkSourceState(invoice: Document, businessStateCode: string): void {
  const hasAnyState = Boolean(
    invoice.placeOfSupplyCode || invoice.deliveryStateCode || invoice.partySnapshot.stateCode,
  );
  if (!hasAnyState) return;
  const resolved = resolvePlaceOfSupply({
    businessStateCode,
    customerStateCode: invoice.partySnapshot.stateCode,
    deliveryStateCode: invoice.deliveryStateCode,
    overrideStateCode: invoice.placeOfSupplyCode,
  });
  if (!resolved.resolved) {
    throw new ReturnServiceError(
      'INVALID_PLACE_OF_SUPPLY',
      `The source invoice has an invalid place-of-supply state (${resolved.reason}). Correct the original sale first.`,
    );
  }
}

/**
 * Read the invoice being corrected, with what is left to return on each line.
 *
 * THE ONE SUBTLE LINE IN THIS FILE
 * -------------------------------
 * `invoicedQty` is set to the invoice line's OWN quantity. That looks redundant —
 * it is the line we are reading the quantity off — and it is not. The fulfilment
 * engine derives `returnableQty` as `invoiced - returned`, and it sources the
 * "invoiced" figure from an INCOMING link when no explicit value is given. An
 * invoice line has no incoming invoicing link (it is the top of that chain, or it
 * consumed a Delivery Note), so without this the engine would read the invoice as
 * having invoiced nothing, `returnableQty` would be ZERO, and EVERY return
 * against EVERY invoice would be refused with a message about over-return.
 *
 * Stating the quantity the line actually sold makes the engine's existing rule
 * answer the question it was written to answer. No new rule was added.
 */
async function readSource(
  db: DNRDatabase,
  invoiceId: string,
  businessId: string,
  excludeReturnDocId?: string,
): Promise<ReturnSource> {
  const invoice = await db.documents.get(invoiceId);
  if (!invoice) {
    throw new ReturnServiceError('SOURCE_NOT_FOUND', 'The invoice could not be found.');
  }
  if (invoice.docType !== 'SALES_INVOICE') {
    // Only a real sale can be credited back. Correcting a quotation or an order
    // is a different act with different consequences, and allowing it here would
    // produce a tax document against something that was never billed.
    throw new ReturnServiceError(
      'WRONG_SOURCE_TYPE',
      'A credit note or sales return must be raised against an invoice.',
    );
  }
  if (invoice.businessId !== businessId) {
    throw new ReturnServiceError('WRONG_BUSINESS', 'That invoice belongs to another business.');
  }
  if (
    invoice.status !== 'ISSUED' &&
    invoice.status !== 'PARTIALLY_SETTLED' &&
    invoice.status !== 'SETTLED'
  ) {
    throw new ReturnServiceError(
      'SOURCE_NOT_ISSUED',
      'Only an issued invoice can be credited or returned. Issue the invoice first.',
    );
  }
  const business = await db.businesses.get(businessId);
  if (!business) {
    throw new ReturnServiceError('NO_BUSINESS', 'Select a business before creating a return.');
  }
  checkSourceState(invoice, business.stateCode);

  const [sourceLines, links, stockLocations] = await Promise.all([
    db.documentLines
      .where('[documentId+lineNo]')
      .between([invoice.id, -Infinity], [invoice.id, Infinity])
      .toArray(),
    db.documentLinks.where('toDocumentId').equals(invoice.id).toArray(),
    db.stockLocations.where('businessId').equals(businessId).toArray(),
  ]);
  // Where returned goods land when the invoice line does not say.
  //
  // This is NOT a convenience fallback. `invoiceService` deliberately treats a line
  // with no location as NOT stock-owning ("a silent default would deduct stock the
  // user never said was leaving the shop"), so an invoice raised through the UI
  // carries no location at all. Refusing every return for that reason would make
  // the physical half of this milestone unreachable for real users, while
  // defaulting would violate the invoice's own rule.
  //
  // The two are reconciled by asking a DIFFERENT question. The invoice is silent
  // about where goods WENT; the return is told where they came back to. Defaulting
  // the RETURN's inbound location to the shop's own default says nothing the user
  // did not already imply, and it is exactly what `deliveryNoteService` does for
  // the same situation.
  const defaultLocation = stockLocations.find((location) => location.isDefault) ?? stockLocations[0];

  const lines: ReturnableLine[] = [];
  for (const line of sourceLines) {
    const product = line.productId ? await db.products.get(line.productId) : undefined;
    const lineLinks = links
      .filter((link) => link.toLineId === line.id && link.fromDocumentId !== excludeReturnDocId)
      .map((link) => ({
        relation: link.relation,
        qty: asQuantity(link.qtyMilli),
        fromDocType: link.fromDocType,
      }));
    const availability = getLineAvailability(
      {
        lineId: line.id,
        quantity: asQuantity(line.quantityMilli + line.freeQuantityMilli),
        // THE LINE explained above: what this line SOLD is what was invoiced.
        invoicedQty: asQuantity(line.quantityMilli + line.freeQuantityMilli),
        fulfilledQty: asQuantity(line.fulfilledQtyMilli),
      },
      lineLinks,
      { allowInvoiceWithoutDelivery: business.documentSettings.allowInvoiceWithoutDelivery },
    );
    const returned = Number(
      lineLinks
        .filter((link) => link.relation === 'RETURNED_AGAINST')
        .reduce((sum, link) => sum + Number(link.qty), 0),
    );
    lines.push({
      ...line,
      returnableQtyMilli: Number(availability.returnableQty),
      alreadyReturnedQtyMilli: returned,
      isStockTracked: product?.isStockTracked ?? false,
      // Surfaced so the editor and the draft builder agree on where the goods will
      // be booked in, instead of each deciding separately.
      ...(line.stockLocationId || !defaultLocation
        ? {}
        : { stockLocationId: defaultLocation.id }),
    });
  }
  return { invoice, lines };
}

/** The issued invoices that could still be credited or returned. */
export async function listReturnableInvoices(
  db: DNRDatabase,
  businessId: string,
): Promise<Document[]> {
  const page = await listRecentDocuments(db as never, businessId, 200, true, 'SALES_INVOICE');
  return page.documents.filter(
    (document) =>
      document.status === 'ISSUED' ||
      document.status === 'PARTIALLY_SETTLED' ||
      document.status === 'SETTLED',
  );
}

/**
 * One invoice with what is left to return on each line — what the editor reads.
 *
 * The business is resolved by the caller because a screen knows which company is
 * open and this service deliberately does not assume one.
 */
export async function loadInvoiceForReturn(
  db: DNRDatabase,
  invoiceId: string,
  options: { businessId?: string; excludeDocumentId?: string } = {},
): Promise<ReturnSource> {
  const businessId = options.businessId ?? (await resolveActiveBusiness(db)).id;
  return readSource(db, invoiceId, businessId, options.excludeDocumentId);
}

async function buildReturnDraft(
  db: DNRDatabase,
  input: ReturnDocumentInput,
  identity?: Document,
) {
  if (input.docType !== 'CREDIT_NOTE' && input.docType !== 'SALES_RETURN') {
    throw new ReturnServiceError('INVALID_DOC_TYPE', 'Unsupported return document type.');
  }
  assertDate(input.docDate);
  if (!input.lines.length) {
    throw new ReturnServiceError('NO_LINES', 'Choose at least one invoice line to credit or return.');
  }
  const sourceLineIds = input.lines.map((line) => line.sourceLineId);
  if (new Set(sourceLineIds).size !== sourceLineIds.length) {
    throw new ReturnServiceError('INVALID_LINES', 'An invoice line can appear only once on a return.');
  }

  const businessId = identity?.businessId ?? (await resolveActiveBusiness(db)).id;
  const existing = identity ? await db.documents.get(identity.id) : undefined;
  if (
    identity &&
    (!existing ||
      (existing.docType !== 'CREDIT_NOTE' && existing.docType !== 'SALES_RETURN') ||
      existing.status !== 'DRAFT')
  ) {
    throw new ReturnServiceError('NOT_EDITABLE', 'Only a return draft can be edited.');
  }
  if (identity && existing && existing.docType !== input.docType) {
    // A draft cannot be re-typed between the two. A CREDIT NOTE and a SALES
    // RETURN differ in whether goods move; converting one into the other after
    // the fact would hide that decision from the ledger.
    throw new ReturnServiceError(
      'NOT_EDITABLE',
      'This draft was raised as a different document type and cannot be changed.',
    );
  }
  const invoiceId = existing?.originalDocId ?? input.invoiceId;
  if (!invoiceId || (existing?.originalDocId && existing.originalDocId !== input.invoiceId)) {
    throw new ReturnServiceError(
      'SOURCE_LINE_INVALID',
      'A return cannot be reassigned to a different invoice.',
    );
  }

  const source = await readSource(db, invoiceId, businessId, existing?.id);
  const sourceLinesById = new Map(source.lines.map((line) => [line.id, line]));
  const business = await db.businesses.get(businessId);
  if (!business) {
    throw new ReturnServiceError('NO_BUSINESS', 'Select a business before creating a return.');
  }

  const physical = returnsGoods(input.docType);
  const resolvedLines: IssueLineInput[] = input.lines.map((line) => {
    const sourceLine = sourceLinesById.get(line.sourceLineId);
    if (!sourceLine || sourceLine.documentId !== invoiceId) {
      throw new ReturnServiceError(
        'SOURCE_LINE_INVALID',
        'A return line must come from the selected invoice.',
      );
    }
    const totalQuantity = line.quantityMilli + (line.freeQuantityMilli ?? 0);
    if (
      !Number.isSafeInteger(line.quantityMilli) ||
      line.quantityMilli <= 0 ||
      totalQuantity > sourceLine.returnableQtyMilli
    ) {
      throw new ReturnServiceError(
        'INVALID_LINES',
        `Requested quantity exceeds the ${sourceLine.returnableQtyMilli / 1000} still returnable for ${sourceLine.description}.`,
      );
    }
    // Goods coming back must land SOMEWHERE. A tracked line with no stock
    // location would post an IN movement nobody can attribute, so it is refused
    // at draft time rather than producing an unattributable ledger row.
    if (physical && sourceLine.isStockTracked && !sourceLine.stockLocationId) {
      throw new ReturnServiceError(
        'STOCK_LOCATION_REQUIRED',
        `The invoice line for ${sourceLine.description} has no stock location, so the returned goods cannot be booked in.`,
      );
    }
    return {
      productId: sourceLine.productId,
      // Where a return goes: the same location the goods left from. Booked back
      // elsewhere, total on-hand would balance while both locations were
      // individually wrong.
      ...(sourceLine.stockLocationId ? { stockLocationId: sourceLine.stockLocationId } : {}),
      description: sourceLine.description,
      descriptionExtra: sourceLine.descriptionExtra,
      hsnSac: sourceLine.hsnSac,
      unit: sourceLine.unit,
      quantityMilli: line.quantityMilli,
      freeQuantityMilli: line.freeQuantityMilli ?? 0,
      // The INVOICE line's rate and tax, not today's. A return credits back what
      // was charged; re-pricing from the current catalogue would reverse a
      // different amount of tax than the invoice bore, and the customer's credit
      // would not match their original bill.
      ratePaise: sourceLine.ratePaise,
      discountType: sourceLine.discountType,
      discountValue: sourceLine.discountValue,
      taxRateBp: sourceLine.taxRateBp,
      cessRateBp: sourceLine.cessRateBp,
      sourceLineId: sourceLine.id,
      // Only a physical return tells the ledger goods came back. A credit note
      // sets this false even for a tracked item, which is what stops it posting.
      isStockTracked: physical && sourceLine.isStockTracked,
    };
  });

  const deviceId = input.deviceId ?? (await getDeviceId(db));
  const params: IssueParams = {
    businessId,
    businessStateCode: business.stateCode,
    docType: input.docType,
    docDate: input.docDate,
    customerId: source.invoice.customerId,
    // SNAPSHOT FROM THE INVOICE, never re-read from the Party. A return is a
    // statement about a past sale; if the customer has since moved or been
    // renamed, the credit must still describe the invoice it corrects.
    partySnapshot: {
      ...source.invoice.partySnapshot,
      addressLines: [...source.invoice.partySnapshot.addressLines],
    },
    placeOfSupplyCode: source.invoice.placeOfSupplyCode,
    deliveryAddressLines: source.invoice.deliveryAddressLines
      ? [...source.invoice.deliveryAddressLines]
      : undefined,
    deliveryStateCode: source.invoice.deliveryStateCode,
    deliveryStateName: source.invoice.deliveryStateName,
    // The link the fulfilment engine and the audit trail both key on.
    originalDocId: invoiceId,
    lines: resolvedLines,
    ...(input.reason ? { notes: `Reason: ${input.reason}` } : {}),
    ...(input.notes ? { notes: input.notes } : {}),
    roundOff: false,
    status: 'DRAFT',
    ...(existing ? { replaceIdentity: { id: existing.id, docNumber: existing.docNumber } } : {}),
    user: USER,
    now: input.now ?? new Date().toISOString(),
    deviceId,
    fyStartMonth: business.fyStartMonth,
  };

  const result = await db.transaction(
    'rw',
    [
      db.documents,
      db.documentLines,
      db.documentLinks,
      db.stockTransactions,
      db.documentSequences,
      db.auditLogs,
      db.parties,
      db.businesses,
      db.products,
      db.syncOutbox,
    ],
    async () => {
      // Re-read inside the transaction. Between the availability read above and
      // this write, another note could have consumed the same quantity — and a
      // check made outside the transaction is a check that can be stale.
      const current = await db.documents.get(invoiceId);
      if (
        !current ||
        current.docType !== 'SALES_INVOICE' ||
        current.status === 'CANCELLED' ||
        current.businessId !== businessId
      ) {
        throw new ReturnServiceError(
          'SOURCE_NOT_ISSUED',
          'The source invoice must remain issued and belong to this business.',
        );
      }
      return issueDocument(db as never, params);
    },
  );
  return { id: result.id, docNumber: result.docNumber, grandTotalPaise: result.grandTotalPaise };
}

export async function createReturnDraft(db: DNRDatabase, input: ReturnDocumentInput) {
  return buildReturnDraft(db, input);
}

/**
 * Create a return draft for EVERYTHING still returnable on an invoice.
 *
 * The common case by far — a customer brings goods back and the user wants the
 * whole remaining line credited. Anything else is a partial return, which the
 * editor handles by adjusting the quantities afterwards.
 */
export async function convertInvoiceToReturnDraft(
  db: DNRDatabase,
  input: {
    docType: ReturnDocType;
    invoiceId: string;
    docDate?: string;
    now?: string;
    deviceId?: string;
  },
) {
  const businessId = (await resolveActiveBusiness(db)).id;
  const source = await readSource(db, input.invoiceId, businessId);
  const lines = source.lines
    .filter((line) => line.returnableQtyMilli > 0)
    .map((line) => ({ sourceLineId: line.id, quantityMilli: line.returnableQtyMilli }));
  if (!lines.length) {
    throw new ReturnServiceError(
      'NO_RETURNABLE_LINES',
      'Everything on this invoice has already been credited or returned.',
    );
  }
  return buildReturnDraft(db, {
    docType: input.docType,
    invoiceId: input.invoiceId,
    docDate: input.docDate ?? new Date().toISOString().slice(0, 10),
    lines,
    now: input.now,
    deviceId: input.deviceId,
  });
}

export async function updateReturnDraft(
  db: DNRDatabase,
  id: string,
  input: Omit<ReturnDocumentInput, 'docType' | 'invoiceId'>,
) {
  const existing = await db.documents.get(id);
  if (!existing || (existing.docType !== 'CREDIT_NOTE' && existing.docType !== 'SALES_RETURN')) {
    throw new ReturnServiceError('NOT_FOUND', 'Return document not found.');
  }
  if (existing.status !== 'DRAFT') {
    throw new ReturnServiceError('NOT_EDITABLE', 'Issued credit notes and returns are immutable.');
  }
  const invoiceId = existing.originalDocId;
  if (!invoiceId) {
    throw new ReturnServiceError('SOURCE_LINE_INVALID', 'This return has no source invoice.');
  }
  return buildReturnDraft(
    db,
    { ...input, docType: existing.docType, invoiceId },
    existing,
  );
}

/**
 * Issue a credit note or sales return, and settle the invoice it corrects.
 *
 * WHY THE INVOICE IS TOUCHED AT ALL
 * ---------------------------------
 * Issuing is the moment the credit becomes real, and the customer's outstanding
 * must reflect it IMMEDIATELY — not the next time some unrelated receipt happens
 * to recompute the invoice. So the invoice's DERIVED rollup (allocated, balance,
 * status) is refreshed here, in the same transaction.
 *
 * Only the derived fields move. The invoice's number, date, lines, tax and totals
 * are never rewritten: it is the historical record of a sale that genuinely
 * happened, and a return does not make it untrue. Receipts recorded against it
 * are untouched, and the credit simply reduces what is left owing.
 *
 * WHY THIS DOES NOT USE `cancelDocument`
 * -------------------------------------
 * Cancellation means the invoice was WRONG. It refuses to run against a document
 * that has payments, and it marks the invoice CANCELLED. A return means the sale
 * was RIGHT and the goods came back — the invoice must remain a live, paid,
 * settled sale. Reaching for cancellation here would destroy the very history the
 * customer is asking to keep.
 */
export async function issueReturnDocument(
  db: DNRDatabase,
  documentId: string,
  input: { now?: string; deviceId?: string } = {},
) {
  const now = input.now ?? new Date().toISOString();
  const deviceId = input.deviceId ?? (await getDeviceId(db));
  return db.transaction(
    'rw',
    [
      db.documents,
      db.documentLines,
      db.documentLinks,
      db.stockTransactions,
      db.products,
      db.auditLogs,
      db.businesses,
      db.payments,
      db.paymentAllocations,
      db.syncOutbox,
    ],
    async () => {
      const doc = await db.documents.get(documentId);
      if (!doc || (doc.docType !== 'CREDIT_NOTE' && doc.docType !== 'SALES_RETURN')) {
        throw new ReturnServiceError('NOT_FOUND', 'Return document not found.');
      }
      const result = await issueDraftDocument(db as never, documentId, USER, now, deviceId);

      // ── SETTLE THE SOURCE INVOICE ──────────────────────────────────────
      // Inside the same transaction as the issue and its stock movement, so a
      // failure here cannot leave a credit issued with the customer still owing
      // the full amount.
      const invoiceId = doc.originalDocId;
      const invoice = invoiceId ? await db.documents.get(invoiceId) : undefined;
      if (!invoice) {
        return { ...result, invoiceBalancePaise: null as number | null };
      }

      const allocated = await sumLiveAllocations(db as never, invoice.id);
      const credited = await sumIssuedCredits(db as never, invoice.id);
      const effectiveTotal = Math.max(0, invoice.grandTotalPaise - credited);
      const version = nextVersion(invoice.syncVersion);
      const updated = {
        ...invoice,
        allocatedPaise: allocated,
        balancePaise: outstandingPaise(invoice.grandTotalPaise, allocated, credited),
        status: derivePaymentStatus(effectiveTotal, allocated),
        updatedAt: now,
        updatedBy: USER.id,
        syncVersion: version,
      };
      await db.documents.put(updated);
      // Published, so a second device settles the same invoice rather than
      // waiting for an unrelated receipt to notice.
      await recordBusinessMutations(db as never, [
        {
          businessId: updated.businessId,
          deviceId,
          entityType: 'document',
          entityId: updated.id,
          operation: 'UPSERT',
          version,
          changedAt: now,
          payload: updated as unknown as Record<string, unknown>,
        },
      ]);
      return { ...result, invoiceBalancePaise: updated.balancePaise };
    },
  );
}

export async function deleteReturnDraft(
  db: DNRDatabase,
  id: string,
  input: { now?: string; deviceId?: string } = {},
) {
  const now = input.now ?? new Date().toISOString();
  const deviceId = input.deviceId ?? (await getDeviceId(db));
  return db.transaction(
    'rw',
    [db.documents, db.documentLines, db.documentLinks, db.auditLogs, db.syncOutbox],
    async () => {
      const doc = await db.documents.get(id);
      if (!doc || (doc.docType !== 'CREDIT_NOTE' && doc.docType !== 'SALES_RETURN')) {
        throw new ReturnServiceError('NOT_FOUND', 'Return document not found.');
      }
      if (doc.status !== 'DRAFT') {
        throw new ReturnServiceError('NOT_EDITABLE', 'Only a return draft can be deleted.');
      }
      const children = await db.documentLinks.where('toDocumentId').equals(id).toArray();
      if (children.some((link) => link.fromDocumentId !== id)) {
        throw new ReturnServiceError(
          'HAS_CHILDREN',
          'This return is referenced by another document and cannot be deleted.',
        );
      }
      const lines = await db.documentLines.where('documentId').equals(id).toArray();
      const links = await db.documentLinks.where('fromDocumentId').equals(id).toArray();
      await db.documentLines.where('documentId').equals(id).delete();
      await db.documentLinks.where('fromDocumentId').equals(id).delete();
      await db.documents.delete(id);
      await db.auditLogs.add({
        id: ulid(),
        businessId: doc.businessId,
        entityType: 'DOCUMENT',
        entityId: id,
        action: 'DELETE',
        before: JSON.stringify({ docNumber: doc.docNumber, status: doc.status }),
        userId: USER.id,
        userName: USER.name,
        occurredAt: now,
      });
      // Tombstones, not local-only deletes: physical removal without an outbox
      // DELETE leaves stale lines and links on every other device.
      await recordBusinessMutations(db as never, [
        {
          businessId: doc.businessId,
          deviceId,
          entityType: 'document',
          entityId: id,
          operation: 'DELETE',
          version: nextVersion(doc.syncVersion),
          changedAt: now,
          deletedAt: now,
        },
        ...lines.map((line) => ({
          businessId: line.businessId,
          deviceId,
          entityType: 'documentLine' as const,
          entityId: line.id,
          operation: 'DELETE' as const,
          version: nextVersion(line.syncVersion ?? 1),
          changedAt: now,
          deletedAt: now,
        })),
        ...links.map((link) => ({
          businessId: link.businessId,
          deviceId,
          entityType: 'documentLink' as const,
          entityId: link.id,
          operation: 'DELETE' as const,
          version: nextVersion(link.syncVersion ?? 1),
          changedAt: now,
          deletedAt: now,
        })),
      ]);
      return { id };
    },
  );
}

/** One list per document type. A credit note is never listed as a return. */
export async function listReturnDocuments(
  db: DNRDatabase,
  businessId: string,
  docType: ReturnDocType,
): Promise<RecentDocumentsPage> {
  return listRecentDocuments(db as never, businessId, 200, true, docType);
}

/** Read one return document with its source invoice, for the detail screen. */
export async function loadReturnDocument(db: DNRDatabase, id: string) {
  const business = await resolveActiveBusiness(db);
  const stored = await db.documents.get(id);
  if (
    !stored ||
    (stored.docType !== 'CREDIT_NOTE' && stored.docType !== 'SALES_RETURN') ||
    stored.businessId !== business.id
  ) {
    throw new ReturnServiceError('NOT_FOUND', 'This return is not available in the active business.');
  }
  const [model, identity, lines, links, invoice] = await Promise.all([
    loadDocument(db as never, id, { businessStateCode: business.stateCode, businessId: business.id }),
    loadBusinessIdentityWithImages(db as never, business.id),
    db.documentLines.where('documentId').equals(id).toArray(),
    db.documentLinks.where('fromDocumentId').equals(id).toArray(),
    stored.originalDocId ? db.documents.get(stored.originalDocId) : undefined,
  ]);
  const sourceLineByReturnLineId = new Map(
    links
      .filter((link) => link.relation === 'RETURNED_AGAINST' && link.toLineId)
      .map((link) => [link.fromLineId ?? '', link.toLineId as string]),
  );
  // The source invoice's NUMBER, ready for the renderer. Resolved here rather
  // than inside `core/doc`, which must never reach into the database to decide
  // what to print — and the stored model carries only the source id.
  const renderOptions = invoice
    ? {
        correctsReferenceNumber: invoice.docNumber,
        // The return-reason code stored on the document is a short structured
        // string (e.g. "DAMAGED", "EXPIRED"). It is passed straight through as
        // the printable credit reason; no code→label mapping lives in the
        // renderer, which cannot interpret a code it has no vocabulary for.
        ...(stored.returnReasonCode ? { creditReason: stored.returnReasonCode } : {}),
      }
    : {};
  return {
    model,
    stored,
    business: identity,
    lines,
    links,
    invoice,
    sourceLineByReturnLineId,
    renderOptions,
  };
}